import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ScCrudService } from '../sc-crud.service';
import { ScBaseTableViewConfig, ScEndpointFilter, ScTableActionSelection, ScCrudConfiguration } from '../../../models/table.model';
import { AddEntity, DeleteEntity, InitView, LoadData, SetData, UpdateEntity } from './view.actions';

const compare = (a: number | string, b: number | string, isAsc: boolean) => (a < b ? -1 : 1) * (isAsc ? 1 : -1);

const localSort = instance => (a, b) => {
  const isAsc = instance.direction === 'asc';
  const column = instance.config.columns.find(c => {
    const sortKey = c.sortKey ? c.sortKey : c.prop;
    return sortKey === instance.activeSort;
  });

  if (column && column.sortCompare) {
    return column.sortCompare(a[instance.activeSort], b[instance.activeSort], isAsc);
  }
  return compare(a[instance.activeSort], b[instance.activeSort], isAsc);
};

export interface ViewStateModel {
  instances: Map<
    string,
    {
      config: ScBaseTableViewConfig;
      datasource: MatTableDataSource<any>;
      filters: ScEndpointFilter[];
      advancedFilters: ScEndpointFilter[];
      page: number;
      activeSort: string;
      direction: 'asc' | 'desc';
      pageSize: number;
      data: any[];
      tmpData: any[];
      loading: boolean;
      selectedAction: ScTableActionSelection;
      totalCount: number;
    }
  >;
}

const VIEW_STATE_TOKEN = new StateToken<ViewStateModel>('view');

const defaultState: ViewStateModel = {
  instances: new Map()
};

// @dynamic
@State({
  name: VIEW_STATE_TOKEN,
  defaults: defaultState
})
@Injectable()
export class ViewState {
  constructor(private _crudService: ScCrudService) {}

  @Selector()
  static getData(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        return state.instances.get(instanceId).data;
      } catch (error) {
        return [];
      }
    };
  }

  @Selector()
  static crudConfig(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        return state.instances.get(instanceId).config.crud;
      } catch (error) {
        return null;
      }
    };
  }

  @Selector()
  static filters(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        return state.instances.get(instanceId).filters;
      } catch (error) {
        return [];
      }
    };
  }

  @Selector()
  static advancedFilters(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        return state.instances.get(instanceId).advancedFilters;
      } catch (error) {
        return [];
      }
    };
  }

  @Selector()
  static totalAdvancedFilters(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        return state.instances.get(instanceId).advancedFilters.length;
      } catch (error) {
        return 0;
      }
    };
  }

  @Selector()
  static filtersFormValue(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        const instance = state.instances.get(instanceId);
        return instance.filters.reduce((prev, curr) => {
          const configFilter = (instance.config.filters || []).find(({ filter }) => filter.prop === curr.prop);
          prev[configFilter.field.key] = curr.value;
          return prev;
        }, {});
      } catch (error) {
        return null;
      }
    };
  }

  @Selector()
  static paginatorData(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        const instance = state.instances.get(instanceId);
        return {
          totalCount: instance.totalCount || 0,
          page: instance.page || 0,
          pageSize: instance.pageSize || 5
        };
      } catch (error) {
        return null;
      }
    };
  }

  @Selector()
  static sortData(state: ViewStateModel) {
    return (instanceId: string) => {
      try {
        const instance = state.instances.get(instanceId);
        return {
          activeSort: instance.activeSort || '',
          direction: instance.direction || 'desc'
        };
      } catch (error) {
        return null;
      }
    };
  }

  @Selector()
  static loading(state: ViewStateModel) {
    return (instanceId: string): boolean => {
      try {
        return state.instances.get(instanceId).loading;
      } catch (error) {
        return false;
      }
    };
  }

  @Action(InitView)
  initView(ctx: StateContext<ViewStateModel>, action: InitView) {
    if (!action.instanceId) {
      return;
    }
    const state = ctx.getState();
    let instance = state.instances.get(action.instanceId);
    const defaultSortColumn = action.config.columns.find(c => c.isDefaultSort);
    ctx.setState({
      ...state,
      instances: state.instances.set(action.instanceId, {
        ...instance,
        config: action.config,
        activeSort: defaultSortColumn ? defaultSortColumn.sortKey || (defaultSortColumn.prop as string) : 'Tstamp',
        direction: defaultSortColumn ? defaultSortColumn.startSortDirection : 'desc',
        datasource: action.datasource
      })
    });

    if (action.config.crud) {
      instance = state.instances.get(action.instanceId);
      return ctx.dispatch(
        new LoadData(
          action.instanceId,
          instance ? instance.page || 0 : 0,
          instance ? instance.pageSize || 5 : 5,
          instance ? instance.filters || [] : [],
          instance ? instance.advancedFilters || [] : [],
          instance ? instance.activeSort || 'Tstamp' : 'Tstamp',
          instance ? instance.direction || 'desc' : 'desc'
        )
      );
    }
  }

  @Action(SetData)
  setData(ctx: StateContext<ViewStateModel>, action: SetData) {
    if (!action.instanceId) {
      return;
    }
    const state = ctx.getState();
    const instance = state.instances.get(action.instanceId);
    const page = instance ? instance.page || 0 : 0;
    const pageSize = instance ? instance.pageSize || 5 : 5;
    instance.datasource.data = action.data.slice(page * pageSize, page * pageSize + pageSize).sort(localSort(instance));
    ctx.setState({
      ...state,
      instances: state.instances.set(action.instanceId, {
        ...instance,
        data: instance.datasource.data,
        tmpData: action.data,
        page,
        pageSize,
        totalCount: action.data.length
      })
    });
  }

  @Action(AddEntity)
  addEntity(ctx: StateContext<ViewStateModel>, action: AddEntity) {
    if (!action.instanceId) {
      return;
    }
    const state = ctx.getState();
    const instance = state.instances.get(action.instanceId);
    instance.datasource.data = [action.row, ...instance.datasource.data];
    ctx.setState({
      ...state,
      instances: state.instances.set(action.instanceId, {
        ...instance,
        data: instance.datasource.data,
        totalCount: instance.totalCount + 1
      })
    });
  }

  @Action(DeleteEntity)
  deleteEntity(ctx: StateContext<ViewStateModel>, action: DeleteEntity) {
    if (!action.instanceId) {
      return;
    }
    const state = ctx.getState();
    const instance = state.instances.get(action.instanceId);
    instance.datasource.data = instance.datasource.data.filter(row => row.id !== action.row.id);
    ctx.setState({
      ...state,
      instances: state.instances.set(action.instanceId, {
        ...instance,
        data: instance.datasource.data,
        totalCount: instance.totalCount - 1
      })
    });
  }

  @Action(UpdateEntity)
  updateEntity(ctx: StateContext<ViewStateModel>, action: UpdateEntity) {
    if (!action.instanceId) {
      return;
    }
    const state = ctx.getState();
    const instance = state.instances.get(action.instanceId);
    instance.datasource.data = instance.datasource.data.map(row => {
      if (row.id === action.row.id) {
        return action.row;
      }
      return row;
    });
    ctx.setState({
      ...state,
      instances: state.instances.set(action.instanceId, {
        ...instance,
        data: instance.datasource.data
      })
    });
  }

  @Action(LoadData)
  loadData(ctx: StateContext<ViewStateModel>, action: LoadData) {
    if (!action.instanceId) {
      return;
    }
    const state = ctx.getState();
    const instance = state.instances.get(action.instanceId);
    instance.loading = true;
    ctx.setState({
      ...state,
      instances: state.instances.set(action.instanceId, {
        ...instance,
        filters: [],
        advancedFilters: []
      })
    });

    const crud: Partial<ScCrudConfiguration> = instance.config.crud;

    if (!crud) {
      instance.loading = false;

      instance.datasource.data = instance.tmpData
        .slice(action.page * action.pageSize, action.page * action.pageSize + action.pageSize)
        .sort(localSort(instance));

      return ctx.setState({
        ...state,
        instances: state.instances.set(action.instanceId, {
          ...instance,
          pageSize: action.pageSize,
          page: action.page,
          data: instance.datasource.data,
          filters: action.filters,
          advancedFilters: action.advancedFilters,
          activeSort: action.sortProp,
          direction: action.direction
        })
      });
    }

    return this._crudService
      .load({
        config: crud.load,
        pageSize: action.pageSize,
        page: action.page + 1,
        filters: [...(crud.load.filters || []), ...action.filters, ...action.advancedFilters],
        itemId: crud.itemId,
        sortProp: action.sortProp,
        direction: action.direction
      })
      .pipe(
        catchError(err => of({ items: [], totalCount: [] })),
        tap(({ items, totalCount }) => {
          instance.loading = false;
          instance.datasource.data = items;
          ctx.setState({
            ...state,
            instances: state.instances.set(action.instanceId, {
              ...instance,
              pageSize: action.pageSize,
              page: action.page,
              data: items,
              totalCount,
              filters: action.filters,
              advancedFilters: action.advancedFilters
            })
          });
        })
      );
  }
}
